import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  forwardRef,
  HostListener,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { createPopper, Instance } from '@popperjs/core';
import { DropdownItem } from '../../models/dropdown-item';
import { MatIcon } from '@angular/material/icon';
import { InputSize } from '../../../enum/input-size.enum';
import { GeneralStatusPillComponent } from '../../../pills/general-status-pill/general-status-pill.component';

@Component({
  selector: 'lib-dropdown-input',
  templateUrl: './dropdown-input.component.html',
  styleUrls: ['./dropdown-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DropdownInputComponent),
      multi: true,
    },
  ],
  imports: [MatIcon, GeneralStatusPillComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DropdownInputComponent implements ControlValueAccessor {
  dropdownItems = input<DropdownItem[]>([]);
  placeholder = input<string | null | undefined>('Select');
  clearable = input<boolean>(false);
  error = input<boolean>(false);
  controlId = input<string | null>(null);
  inputSize = input<InputSize>(InputSize.large);
  dropdownMatchesInputWidth = input<boolean>(true);

  disabled = signal<boolean>(false);

  dropdownContent = viewChild<ElementRef<HTMLDivElement>>('dropdownContent');
  dropdownInput = viewChild<ElementRef<HTMLDivElement>>('dropdownInput');

  private pendingValue = signal<unknown>(undefined);
  private popperInstance: Instance | undefined;

  public selectedItem = signal<DropdownItem | null>(null);
  public showDropdown = signal(false);
  public highlightedIndex = signal(-1);
  public adjustedHighlightedIndex = computed(() => {
    if (this.selectedItem() !== null && this.clearable()) {
      return this.highlightedIndex() - 1;
    } else {
      return this.highlightedIndex();
    }
  });

  private dropdownItemsEffect = effect(() => {
    this.trySelectValue();
  });

  protected readonly InputSize = InputSize;
  onChange = (_: unknown) => {
    //
  };
  onTouched = () => {
    //
  };

  writeValue(obj: unknown): void {
    this.pendingValue.set(obj);
  }

  registerOnChange(fn: (_: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  public toggleDropdown(): void {
    if (!this.disabled()) {
      if (this.showDropdown()) {
        this.popperInstance?.destroy();
      } else {
        setTimeout(() => {
          this.positionDropdown();
        });
      }
      this.showDropdown.update(v => !v);
    }
  }

  public closeDropdown(): void {
    setTimeout(() => {
      // This is a hack:
      // This setTimeout prevents a second dropdown input position from being incorrect
      this.showDropdown.set(false);
      this.popperInstance?.destroy();
    });
  }

  public itemSelected(item: DropdownItem): void {
    if (item.disabled) {
      return;
    }

    this.selectedItem.set(item);
    this.onChange(item.value);
    this.onTouched();
    this.closeDropdown();
  }

  public resetInput(): void {
    this.selectedItem.set(null);
    this.onChange(null);
    this.onTouched();
  }

  // Keyboard handling
  @HostListener('keydown', ['$event'])
  handleKeydown(event: KeyboardEvent) {
    let totalItemsCount = this.dropdownItems().length;
    const adjustForResetButton = this.selectedItem() !== null && this.clearable();
    if (adjustForResetButton) {
      totalItemsCount = totalItemsCount + 1;
    }

    if (!this.showDropdown()) {
      if (event.key === ' ' || event.key === 'Spacebar') {
        this.toggleDropdown();
      }
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.highlightedIndex.update(i => (i + 1) % totalItemsCount);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.highlightedIndex.update(i => (i - 1 + totalItemsCount) % totalItemsCount);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      if (adjustForResetButton && this.highlightedIndex() === 0) {
        this.resetInput();
        this.closeDropdown();
      } else if (this.highlightedIndex() >= 0) {
        this.itemSelected(this.dropdownItems()[this.adjustedHighlightedIndex()]);
      }
    } else if (event.key === 'Escape') {
      this.closeDropdown();
    } else if (event.key === ' ' || event.key === 'Spacebar') {
      this.toggleDropdown();
    }
  }

  private positionDropdown(): void {
    const dropdownContent = this.dropdownContent();
    const dropdownInput = this.dropdownInput();
    if (!dropdownContent || !dropdownInput) return;
    const dropdownElement = dropdownContent.nativeElement;
    const parentTables = document.getElementsByClassName('data-table-container');
    this.popperInstance = createPopper(dropdownInput.nativeElement, dropdownElement, {
      placement: 'bottom-start',
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, 8],
          },
        },
        {
          name: 'eventListeners',
          enabled: true,
          options: {
            scroll: true,
            resize: true,
          },
        },
        {
          name: 'flip',
          options: {
            altBoundary: true,
            padding: { bottom: 24 },
          },
        },
        {
          name: 'preventOverflow',
          options: {
            boundary: parentTables[0],
          },
        },
      ],
      strategy: 'fixed',
    });
    if (this.dropdownMatchesInputWidth()) {
      dropdownElement.style.width = `${dropdownInput.nativeElement.clientWidth}px`;
    }
  }

  private trySelectValue(): void {
    const pendingValue = this.pendingValue();
    if (!this.dropdownItems()?.length || pendingValue === undefined) {
      return;
    }
    const match = this.dropdownItems().find(d => d.value === pendingValue);
    if (match) {
      this.selectedItem.set(match);
    } else {
      this.selectedItem.set(null);
    }
    this.pendingValue.set(undefined);
  }
}
